import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../Components/Layout/Layout";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Cookie helpers
const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
};
const getCookie = (name) => {
  const m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
  return m ? decodeURIComponent(m.pop()) : null;
};

export default function TeamList() {
  const navigate = useNavigate();
  const getAuthToken = () => localStorage.getItem("auth_token") || "";

  // Data state
  const [teams, setTeams] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locOptions, setLocOptions] = useState([]);
  const [schedules, setSchedules] = useState([]); // New state for schedules
  const [loading, setLoading] = useState(true);

  // UI state
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [detailModalTitle, setDetailModalTitle] = useState("");
  const [detailModalContent, setDetailModalContent] = useState([]);

  // Form fields
  const [newName, setNewName] = useState("");
  const [deptId, setDeptId] = useState("");
  const [newLocException, setNewLocException] = useState(false);
  const [selectedSchedules, setSelectedSchedules] = useState([]); // Replaced newStartTimes
  const [selectedResponsibles, setSelectedResponsibles] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedLocs, setSelectedLocs] = useState([]);

  // Toggle for showing deactivated users in selectors (persisted in cookie)
  const [showInactive, setShowInactive] = useState(false);
  useEffect(() => {
    const v = getCookie("show_inactive_users");
    setShowInactive(v === "1");
  }, []);
  const toggleShowInactive = (e) => {
    const v = e.target.checked;
    setShowInactive(v);
    setCookie("show_inactive_users", v ? "1" : "0");
  };

  // Fetch data
  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        console.error("No auth token found in localStorage");
        setTeams([]);
        return;
      }
      const res = await fetch(`${API_URL}/admin/teams?auth_token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (data.status === "success") {
        setTeams(data.teams || []);
      } else {
        console.error("Failed to fetch teams:", data.message);
        setTeams([]);
      }
    } catch (err) {
      console.error("Network error fetching teams:", err);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDeps = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/admin/departments?auth_token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (data.status === "success") setDepartments(data.departments || []);
    } catch (err) {
      console.error("Error fetching departments:", err);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/admin/users?auth_token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (data.status === "success") setEmployees(data.users || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/allowed-locations`);
      const j = await res.json();
      const pts = (j.points || []).map((p) => ({
        id: `P${p.id}`,
        label: `📍 ${p.name || `Zone #${p.id}`}`,
      }));
      const polys = (j.polygons || []).map((a) => ({
        id: `A${a.id}`,
        label: `🔷 ${a.name || `Area #${a.id}`}`,
      }));
      setLocOptions([...pts, ...polys]);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/admin/schedules?auth_token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (data.status === "success") {
        setSchedules(data.schedules || []);
      }
    } catch (err) {
      console.error("Error fetching schedules:", err);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
    fetchDeps();
    fetchEmployees();
    fetchLocations();
    fetchSchedules();
  }, [fetchTeams, fetchDeps, fetchEmployees, fetchLocations, fetchSchedules]);

  // Utility functions
  const getDepartmentName = (id) => {
    const d = departments.find((x) => String(x.id) === String(id));
    return d ? d.name : id || "-";
  };

  // === FIX #1: parseEmployees — понимает id ИЛИ chat_id ===
  const parseEmployees = (csv) => {
    if (!csv) return [];
    const tokens = csv.split(",").map((s) => s.trim()).filter(Boolean);
    return tokens
      .map((tok) => {
        let emp = employees.find((e) => String(e.id) === tok);
        if (!emp) emp = employees.find((e) => String(e.chat_id) === tok);
        return emp
          ? `${emp.first_name ?? ""} ${emp.last_name ?? ""}`.trim() || emp.email || emp.phone || tok
          : tok;
      })
      .filter(Boolean);
  };

  const parseLocations = (idsStr) => {
    if (!idsStr) return [];
    return idsStr
      .split(",")
      .map((id) => {
        const opt = locOptions.find((o) => o.id === id.trim());
        return opt ? opt.label.replace(/^.\s/, "") : id.trim();
      })
      .filter(Boolean);
  };

  const getConciseList = (arr = []) =>
    arr.length > 3 ? `${arr.slice(0, 3).join(", ")} …` : arr.join(", ");

  const handleOpenDetailModal = (title, items) => {
    setDetailModalTitle(title);
    setDetailModalContent(items);
    setOpenDetailModal(true);
  };

  const resetCreateForm = () => {
    setNewName("");
    setDeptId("");
    setNewLocException(false);
    setSelectedSchedules([]);
    setSelectedResponsibles([]);
    setSelectedMembers([]);
    setSelectedLocs([]);
  };

  const handleCreate = () => {
    resetCreateForm();
    setOpenCreate(true);
  };

  const handleCreateSave = async () => {
    if (!deptId) return alert("Please select a department");
    const token = getAuthToken();
    const body = {
      auth_token: token,
      name: newName,
      department_id: deptId,
      location_exception: newLocException,
      start_times: selectedSchedules.map((s) => s.schedule_id).join(","), // Use schedule IDs
      responsibles: selectedResponsibles.map((u) => u.id).join(","),      // сохраняем только ID
      members: selectedMembers.map((u) => u.id).join(","),                // сохраняем только ID
      allowed_locations: selectedLocs.map((o) => o.id).join(","),
    };
    try {
      const res = await fetch(`${API_URL}/admin/teams`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchTeams();
        setOpenCreate(false);
        resetCreateForm();
      } else {
        console.error("Create team error:", data.message);
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      console.error("Create team fetch error:", err);
      alert("Network error creating team.");
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setNewName(team.name || "");
    setDeptId(team.department_id || "");
    setNewLocException(!!team.location_exception);
    // Populate selected schedules
    const teamSchedules = team.schedules || [];
    setSelectedSchedules(
      teamSchedules.map((s) => schedules.find((sched) => sched.schedule_id === s.schedule_id)).filter(Boolean)
    );

    // === FIX #2: умный toSel — ищет по id ИЛИ по chat_id ===
    const toSel = (csv, opts) => {
      if (!csv) return [];
      const tokens = csv.split(",").map((i) => i.trim()).filter(Boolean);
      return tokens
        .map((tok) => {
          let found = opts.find((o) => String(o.id) === tok);
          if (found) return found;
          found = opts.find((o) => String(o.chat_id) === tok);
          return found || null;
        })
        .filter(Boolean);
    };

    setSelectedResponsibles(toSel(team.responsibles, employees));
    setSelectedMembers(toSel(team.members, employees));

    const toSelLocs = (csv, opts) => {
      if (!csv) return [];
      const tokens = csv.split(",").map((i) => i.trim()).filter(Boolean);
      return tokens.map((id) => opts.find((o) => String(o.id) === String(id))).filter(Boolean);
    };
    setSelectedLocs(toSelLocs(team.allowed_locations, locOptions));
    setOpenEdit(true);
  };

  const handleEditSave = async () => {
    if (!deptId) return alert("Please select a department");
    const token = getAuthToken();
    const body = {
      auth_token: token,
      name: newName,
      department_id: deptId,
      location_exception: newLocException,
      start_times: selectedSchedules.map((s) => s.schedule_id).join(","), // Use schedule IDs
      responsibles: selectedResponsibles.map((u) => u.id).join(","),      // только ID
      members: selectedMembers.map((u) => u.id).join(","),                // только ID
      allowed_locations: selectedLocs.map((o) => o.id).join(","),
    };
    try {
      const res = await fetch(`${API_URL}/admin/teams/${editingTeam.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchTeams();
        setOpenEdit(false);
        setEditingTeam(null);
      } else {
        console.error("Edit team error:", data.message);
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      console.error("Edit team fetch error:", err);
      alert("Network error editing team.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this team?")) {
      return;
    }
    const token = getAuthToken();
    try {
      const res = await fetch(`${API_URL}/admin/teams/${id}?auth_token=${encodeURIComponent(token)}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchTeams();
      } else {
        console.error("Delete team error:", data.message);
        alert(`Failed to delete team: ${data.message}`);
      }
    } catch (err) {
      console.error("Network error deleting team:", err);
      alert("Network error deleting team. Please try again.");
    }
  };

  // ===== Employee options (hide inactive by default, persist toggle via cookie) =====
  // === FIX #3: учитываем и is_active, и active; по умолчанию не режем, если оба undefined ===
  const employeeBaseOptions = useMemo(() => {
    if (showInactive) return employees;
    return employees.filter((u) => {
      const flag = u.is_active ?? u.active;
      return flag === undefined ? true : Boolean(flag);
    });
  }, [employees, showInactive]);

  // Сохраняем выбранных в списке опций, даже если они уже не проходят фильтр
  const mergeSelected = (options, selected) => {
    const map = new Map(options.map((u) => [String(u.id), u]));
    selected.forEach((u) => {
      const k = String(u.id);
      if (!map.has(k)) map.set(k, u);
    });
    return Array.from(map.values());
  };

  const responsibleOptions = useMemo(
    () => mergeSelected(employeeBaseOptions, selectedResponsibles),
    [employeeBaseOptions, selectedResponsibles]
  );
  const memberOptions = useMemo(
    () => mergeSelected(employeeBaseOptions, selectedMembers),
    [employeeBaseOptions, selectedMembers]
  );

  const userRole = localStorage.getItem("userRole") || "";
  const handleView = (team) => navigate(`/admin/entity_timesheet_detail/team/${team.id}`);

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5">Teams</Typography>

        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={<Checkbox checked={showInactive} onChange={toggleShowInactive} />}
            label="Show deactivated users (affects selectors)"
          />
        </Box>

        {["ceo", "admin"].includes(userRole.toLowerCase()) && (
          <Button variant="contained" sx={{ mt: 2 }} onClick={handleCreate}>
            Create Team
          </Button>
        )}

        {loading ? (
          <Typography sx={{ mt: 2 }}>Loading...</Typography>
        ) : teams.length === 0 ? (
          <Typography sx={{ mt: 2 }}>No teams available</Typography>
        ) : (
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Team Name</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Location Exception</TableCell>
                <TableCell>Schedules</TableCell>
                <TableCell>Responsibles</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Allowed Locations</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teams.map((t) => {
                const respNames = parseEmployees(t.responsibles);
                const memberNames = parseEmployees(t.members);
                const locNames = parseLocations(t.allowed_locations);
                return (
                  <TableRow key={t.id}>
                    <TableCell>{t.id}</TableCell>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>{getDepartmentName(t.department_id)}</TableCell>
                    <TableCell>{t.location_exception ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      {(t.schedules || []).map((s) => (
                        <Chip
                          key={s.schedule_id}
                          label={`${s.start_times} (${s.days_of_week})`}
                          sx={{ mr: 1, mb: 1 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer", color: "blue" }}
                      onClick={() => handleOpenDetailModal("Responsibles", respNames)}
                    >
                      {respNames.length ? getConciseList(respNames) : "-"}
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer", color: "blue" }}
                      onClick={() => handleOpenDetailModal("Members", memberNames)}
                    >
                      {memberNames.length ? getConciseList(memberNames) : "-"}
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer", color: "blue" }}
                      onClick={() => handleOpenDetailModal("Allowed Locations", locNames)}
                    >
                      {locNames.length ? getConciseList(locNames) : "-"}
                    </TableCell>
                    <TableCell>
                      <Button variant="outlined" onClick={() => handleView(t)} sx={{ mr: 1 }}>
                        <VisibilityIcon fontSize="small" /> View
                      </Button>
                      <Button variant="outlined" onClick={() => handleEdit(t)} sx={{ mr: 1 }}>
                        Edit
                      </Button>
                      <Button color="error" onClick={() => handleDelete(t.id)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Dialog open={openDetailModal} onClose={() => setOpenDetailModal(false)}>
          <DialogTitle>{detailModalTitle}</DialogTitle>
          <DialogContent>
            {detailModalContent.length ? (
              detailModalContent.map((n, i) => <Typography key={i}>{n}</Typography>)
            ) : (
              <Typography>No items available</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetailModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
          <DialogTitle>Create Team</DialogTitle>
          <DialogContent>
            <TextField
              label="Team Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />
            <Select fullWidth sx={{ mt: 2 }} value={deptId} onChange={(e) => setDeptId(e.target.value)} displayEmpty>
              <MenuItem value="">
                <em>Select Department</em>
              </MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
            <FormControlLabel
              sx={{ mt: 2 }}
              control={<Checkbox checked={newLocException} onChange={(e) => setNewLocException(e.target.checked)} />}
              label="Location Exception"
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={schedules}
              getOptionLabel={(o) => `${o.start_times.replace(",", "-")} (${o.days_of_week})`}
              value={selectedSchedules}
              onChange={(e, v) => setSelectedSchedules(v)}
              renderInput={(params) => <TextField {...params} label="Schedules" />}
            />
            <Autocomplete
              multiple
              options={responsibleOptions}
              getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={selectedResponsibles}
              onChange={(e, v) => setSelectedResponsibles(v)}
              renderInput={(params) => <TextField {...params} label="Responsibles" sx={{ mt: 2 }} />}
            />
            <Autocomplete
              multiple
              options={memberOptions}
              getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={selectedMembers}
              onChange={(e, v) => setSelectedMembers(v)}
              renderInput={(params) => <TextField {...params} label="Members" sx={{ mt: 2 }} />}
            />
            <Autocomplete
              multiple
              options={locOptions}
              getOptionLabel={(o) => o.label}
              value={selectedLocs}
              onChange={(e, v) => setSelectedLocs(v)}
              renderInput={(params) => <TextField {...params} label="Allowed Locations" sx={{ mt: 2 }} />}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateSave}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
          <DialogTitle>Edit Team</DialogTitle>
          <DialogContent>
            <TextField
              label="Team Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />
            <Select fullWidth sx={{ mt: 2 }} value={deptId} onChange={(e) => setDeptId(e.target.value)} displayEmpty>
              <MenuItem value="">
                <em>Select Department</em>
              </MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
            <FormControlLabel
              sx={{ mt: 2 }}
              control={<Checkbox checked={newLocException} onChange={(e) => setNewLocException(e.target.checked)} />}
              label="Location Exception"
            />
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={schedules}
              getOptionLabel={(o) => `${o.start_times.replace(",", "-")} (${o.days_of_week})`}
              value={selectedSchedules}
              onChange={(e, v) => setSelectedSchedules(v)}
              renderInput={(params) => <TextField {...params} label="Schedules" />}
            />
            <Autocomplete
              multiple
              options={responsibleOptions}
              getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={selectedResponsibles}
              onChange={(e, v) => setSelectedResponsibles(v)}
              renderInput={(params) => <TextField {...params} label="Responsibles" sx={{ mt: 2 }} />}
            />
            <Autocomplete
              multiple
              options={memberOptions}
              getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={selectedMembers}
              onChange={(e, v) => setSelectedMembers(v)}
              renderInput={(params) => <TextField {...params} label="Members" sx={{ mt: 2 }} />}
            />
            <Autocomplete
              multiple
              options={locOptions}
              getOptionLabel={(o) => o.label}
              value={selectedLocs}
              onChange={(e, v) => setSelectedLocs(v)}
              renderInput={(params) => <TextField {...params} label="Allowed Locations" sx={{ mt: 2 }} />}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSave}>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
